import { useEffect, useRef } from 'react';

/**
 * A custom hook that wraps a callback function with requestAnimationFrame.
 * This ensures the callback executes in sync with the browser's repaint cycle,
 * providing smooth animations and optimal performance.
 *
 * @param callback - The function to be executed within requestAnimationFrame
 * @param deps - Dependency array for the callback (similar to useCallback dependencies)
 *
 * @returns A wrapped function that schedules the callback via requestAnimationFrame
 *
 * @example
 * ```tsx
 * const handleUpdate = useCallback(() => {
 *   setState(newValue);
 * }, [newValue]);
 *
 * const animatedUpdate = useAnimationFrame(handleUpdate);
 *
 * // Use animatedUpdate instead of handleUpdate
 * <button onClick={animatedUpdate}>Update</button>
 * ```
 */
export const useAnimationFrame = <T extends (...args: never[]) => void>(
  callback: T
): ((...args: Parameters<T>) => void) => {
  const animationFrameRef = useRef<number | null>(null);
  const callbackRef = useRef(callback);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Return a stable function that uses requestAnimationFrame
  const animatedCallback = useRef((...args: Parameters<T>) => {
    // Cancel any pending animation frame
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Schedule callback to run in sync with browser repaint
    animationFrameRef.current = requestAnimationFrame(() => {
      callbackRef.current(...args);
      animationFrameRef.current = null;
    });
  });

  return animatedCallback.current;
};
